<template>
  <!-- DM-10271: a People-Also-Ask source's Rating / Ownership / Authority chips. Each opens a menu
       with the Markup dialog's options and rules (src/utils/markupOptions.js). Layout and colours
       follow the ticket's prototype. -->
  <template v-if="source.urlid">
    <div v-for="type in visibleTypes" :key="type.name" class="chip-wrapper">
      <button type="button" class="markup-chip" :data-chips-of="$.uid" :disabled="!canEdit(type)"
        :title="chipTitle(type)" :aria-expanded="openType === type.name ? 'true' : 'false'"
        :data-testid="`card-aioverview-paa-source-${type.name}-chip`" @click="toggleMenu(type.name)">
        <span class="dot" :style="dotStyle(currentOption(type))"></span>
        <span class="chip-label">{{ currentName(type) }}</span>
        <svg v-if="canEdit(type)" aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="#40454C" stroke-width="2.6" class="chevron"><path d="M6 9l6 6 6-6"></path></svg>
      </button>

      <div v-if="openType === type.name" v-click-outside="{ handler: closeMenu, middleware: notOnAChip }" class="chip-menu"
        :data-testid="`card-aioverview-paa-source-${type.name}-menu`">
        <div v-if="showScope(type)" class="scope">
          <div class="scope-title">Apply to</div>
          <div class="scope-buttons">
            <button v-for="s in scopes" :key="s.value" type="button" :class="{ on: scope === s.value }"
              :aria-pressed="scope === s.value ? 'true' : 'false'" :title="s.label"
              :data-testid="`card-aioverview-paa-source-mark-${s.value}-btn`" @click="scope = s.value">
              {{ s.label }}
            </button>
          </div>
        </div>
        <button v-for="option in shownOptions(type)" :key="option.value" type="button" class="option"
          :disabled="availability(type, option).disabled" :title="title(type, option)"
          :style="optionStyle(type, option)"
          :data-testid="`card-aioverview-paa-source-${type.name}-${option.name.toLowerCase().replace(/\s+/g, '-')}-btn`"
          @click="pick(type, option)">
          <span class="dot" :style="dotStyle(option)"></span>
          <span>{{ option.name }}</span>
        </button>
      </div>
    </div>
  </template>
</template>

<script>
import vClickOutside from "click-outside-vue3";
import { mapGetters } from "vuex";
import {
  markupTypes, numRatings, usesScope, isGroupVisible, isLocked, isUnmarkedValue, buttonAvailability, buttonTitle,
} from "@/utils/markupOptions";

// the rating's scope, worded as in the ticket's prototype (the Markup dialog's Mark all / backward / forward)
const SCOPES = [
  { value: "all", label: "All dates" },
  { value: "backward", label: "Up to" },
  { value: "forward", label: "From" },
];

export default {
  name: "SourceMarkupChips",
  directives: { clickOutside: vClickOutside.directive },
  // only the Timeline view can change markup (it provides this); elsewhere the chips just show the values
  inject: { markupEditable: { default: false } },
  props: {
    source: { type: Object, required: true }, // { url, urlid, markup }
    resultType: { type: String, default: "questionanswer" },
    ts: { type: Number, default: 0 }, // the card's date, as the Markup dialog uses it
  },
  data() {
    return {
      openType: null,
      scope: "all",
      saving: false,
    };
  },
  computed: {
    ...mapGetters("toolbar", { isMarkupEnabled: "isMarkupEnabled" }),
    ...mapGetters("routeParams", { userInfo: "userInfo" }),
    markup() {
      return this.source.markup || {};
    },
    target() {
      return { url: this.source.url, markup: this.markup, resultType: this.resultType };
    },
    visibleTypes() {
      const context = { isMarkupEnabled: this.isMarkupEnabled, isSu: this.userInfo?.is_su, resultType: this.resultType };
      return markupTypes(this.markup).filter((type) => isGroupVisible(type.name, context));
    },
    scopes() {
      return SCOPES;
    },
  },
  methods: {
    currentOption(type) {
      return type.options.find((o) => o.value === type.value);
    },
    currentName(type) {
      return this.currentOption(type)?.name || "Unmarked";
    },
    heading(type) {
      return type.name.charAt(0).toUpperCase() + type.name.slice(1);
    },
    locked(type) {
      return isLocked(this.target, type.name, type.options);
    },
    canEdit(type) {
      return this.markupEditable && !this.saving && !this.locked(type);
    },
    chipTitle(type) {
      return this.locked(type)
        ? `${this.heading(type)} is locked for owned URLs`
        : `${this.heading(type)}: ${this.currentName(type)}`;
    },
    dotStyle(option) {
      const unmarked = !option || isUnmarkedValue(option.value);
      return { background: unmarked ? "#fff" : option.color, borderColor: unmarked ? "#8D8DA0" : option.color };
    },
    optionStyle(type, option) {
      const selected = option.value === type.value;
      const disabled = this.availability(type, option).disabled;
      return {
        background: selected ? option.fill || "#e7effc" : "#fff",
        fontWeight: selected ? 600 : 400,
        opacity: disabled && !selected ? 0.65 : 1,
        cursor: disabled ? "default" : "pointer",
      };
    },
    shownOptions(type) {
      return type.options.filter((option) => !this.availability(type, option).hidden);
    },
    availability(type, option) {
      return buttonAvailability(this.target, type.name, option);
    },
    title(type, option) {
      return buttonTitle({ url: this.source.url, markup: this.markup }, type.name, option);
    },
    showScope(type) {
      return type.name === "rating" && numRatings(this.markup) > 0;
    },
    toggleMenu(typeName) {
      this.scope = "all";
      this.openType = this.openType === typeName ? null : typeName;
    },
    closeMenu() {
      this.openType = null;
    },
    // a click on one of THIS row's chips switches menus itself (toggleMenu), so it is not a click
    // "outside"; a click anywhere else, another row's chips included, closes the menu
    notOnAChip(event) {
      return event.target?.closest?.(".markup-chip")?.dataset.chipsOf !== String(this.$.uid);
    },
    pick(type, option) {
      if (this.availability(type, option).disabled) return;
      this.save(type.name, option, usesScope(type.name, option, this.markup) ? this.scope : "all");
    },
    save(typeName, option, ratingAction) {
      if (this.saving || !this.markupEditable) return;
      this.openType = null;
      this.saving = true;
      const target = { url: this.source.url, urlid: this.source.urlid, ts: this.ts, resultType: this.resultType };
      this.$store.dispatch("markup/setUrlMarkup", { target, markupType: typeName, newMarkup: option.value, ratingAction })
        .catch((e) => this.$notifier.error({ message: `Failed setting markup ${option.value}`, e }))
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
// sizes and colours from the DM-10271 prototype
.chip-wrapper {
  position: relative;
  flex: 0 0 auto;
}

.markup-chip {
  width: 122px;
  height: 26px;
  box-sizing: border-box;
  border: 1px solid #7B97C7;
  border-radius: 13px;
  background: #fff;
  color: #212529;
  font: inherit;
  padding: 0 7px 0 9px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 1;
  }

  .chip-label {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chevron {
    flex: 0 0 auto;
  }
}

.dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1px solid;
}

.chip-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 3px);
  width: 176px;
  background: #fff;
  border: 1px solid #cdcdcd;
  border-radius: 4px;
  box-shadow: 0 2px 7px rgba(0, 0, 0, .3);
  padding: 4px;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 1px;

  .option {
    min-height: 26px;
    border: 0;
    border-radius: 3px;
    color: #212529;
    font: inherit;
    text-align: left;
    padding: 2px 6px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.scope {
  padding: 2px 4px 6px;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 3px;

  .scope-title {
    font-size: 11px;
    color: #40454C;
    margin-bottom: 3px;
  }

  .scope-buttons {
    display: flex;
    gap: 3px;

    button {
      flex: 1 1 0;
      min-width: 0;
      height: 22px;
      border: 1px solid #cdcdcd;
      border-radius: 3px;
      background: #fff;
      color: #212529;
      font: inherit;
      font-size: 11px;
      padding: 0;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.on {
        border-color: #003C8F;
        background: #003C8F;
        color: #fff;
      }
    }
  }
}
</style>
